// @givmo/design-system — EmptyState (SaaS layer).
//
// The "no data" surface for tables/lists/cards: a centered icon in a tinted circle, a title, a
// one-line helper, and an optional primary action. Used in place of a bare spinner or blank panel
// when a query returns zero rows or a feature has no content yet.
import type { ReactNode } from 'react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';

export interface EmptyStateProps extends TestIdProps {
  /** Leading glyph (Lucide component) rendered in a tinted neutral circle. */
  icon?: ReactNode;
  title: ReactNode;
  /** One-line helper text under the title. */
  description?: ReactNode;
  /** Optional primary action (typically a SaaS Button). */
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
  testId,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-12 px-24 py-44 text-center',
        className,
      )}
      {...testIdProp(testId)}
    >
      {icon != null && (
        <span className="flex size-44 items-center justify-center rounded-full bg-neutral-100 text-icon">
          {icon}
        </span>
      )}
      <div className="flex flex-col gap-4">
        <p className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">{title}</p>
        {description != null && (
          <p className="max-w-[360px] font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
            {description}
          </p>
        )}
      </div>
      {action != null && <div className="mt-4">{action}</div>}
    </div>
  );
}
